import * as React from 'react';
import { connect, Dispatch } from 'react-redux';
import { StoreState } from '../../../reducers';
import { Api } from '../../../typings/api';
import { GalleryInfosState } from '../../Gallery/reducer';
import { viewDriveItem, DriveItemOrigin } from '../../DriveItemList/actions';
import DriveItemUtils, { ActionButton, ActionButtonStyle } from '../../../utils/driveItem';
import Utils from '../../../utils';
import UrlUtils from '../../../utils/url';
import UserAgentUtils from '../../../utils/userAgent';
import { StyledDriveItemDetail, Container,
	Header, HeaderInner, BackgroundImage,
	TitleContainer, Title, SubTitle,
	DescriptionRow, DescriptionLabel, DescriptionText,
	InfoRow, InfoLabel, InfoText, ActionButtons, ActionButtonsInner, StandardButton,
	PublicationCoverContainer, PublicationCoverImage } from './styles';
import { OfflineManagerState } from '../../OfflineManager/reducer';
import DownloadManagerUtils from '../../../utils/downloadManager';
import DriveItemDeleteButton from '../DeleteButton';
import DriveItemDownloadButton from '../DownloadButton';
import { Translate } from 'react-redux-i18n';
const I18n = require('react-redux-i18n').I18n;
const Localize = require('react-redux-i18n').Localize;

const MAX_PUBLICATION_THUMB = 5;

enum Format {
	Date = 'date',
	Locale = 'locale',
	Extension = 'extension'
}

interface DriveItemDetailContainerProps {
	driveItem: Api.DriveItem;
	resolutions: { [k: string]: Api.ResolutionGroup };
}

export interface DriveItemDetailComponentProps {
	driveItem: Api.DriveItem;
	resolutions: { [k: string]: Api.ResolutionGroup };
	dispatch: Dispatch<StoreState>;
	galleryInfos: GalleryInfosState;
	offlineManager: OfflineManagerState;
	isPersistentlyDownloaded: boolean;
	isRequired: boolean;
	isRequested: boolean;
	version: number;
}

class DriveItemDetail extends React.Component<DriveItemDetailComponentProps, {}> {
	constructor(props: DriveItemDetailComponentProps) {
		super(props);
		this.getImgElement = this.getImgElement.bind(this);
		this.getPublicationCovers = this.getPublicationCovers.bind(this);
	}

	getImgElement = () => {
		const { driveItem, offlineManager, galleryInfos, resolutions } = this.props;
		const thumbnailDescriptor = DriveItemUtils.getThumbnailDescriptor(driveItem, false, offlineManager, galleryInfos.config, resolutions);
		const { isImage, src } = thumbnailDescriptor;

		if (isImage) {
			return <BackgroundImage src={src} />;
		} else {
			return Utils.getSvgIcon(src);
		}
	}

	getPublicationCovers = () => {
		const { driveItem, isPersistentlyDownloaded, galleryInfos } = this.props;

		let publicationCoversUrls: string[] = [];
		let publicationCovers: React.ReactNode[] = [];
		if (driveItem.type === Api.DriveItemType.Publication && driveItem.pages && driveItem.thumbsPath) {
			for (let i = 0; i < driveItem.pages && i < MAX_PUBLICATION_THUMB; i++) {
				if (isPersistentlyDownloaded) {
					const path = Utils.getPersistentDownloadsFolder();
					const cover = DriveItemUtils.getPublicationCover(driveItem.thumbsPath, i + 1);
					publicationCoversUrls.push(`${path}/${driveItem.id}/${cover}`);
				} else {
					publicationCoversUrls.push(UrlUtils.getPublicationCoverUrl(galleryInfos.config, driveItem, i));
				}
			}

			publicationCovers = publicationCoversUrls.map((url, i) => <PublicationCoverImage key={i} src={url} />);
		}
		return publicationCovers;
	}

	renderInfoRow = (message: string, data: string, formatting?: Format) => {
		const infoText = () => {
			switch (formatting) {
				case Format.Date:
					return <Localize value={data} dateFormat="date_format.long" />;
				case Format.Locale:
					return <Translate value={data} />;
				default:
					return data;
			}
		};

		return (
			<Container>
				<InfoRow>
					<InfoLabel><Translate value={message} /></InfoLabel>
					<InfoText uppercase={formatting === Format.Extension}>
						{infoText()}
					</InfoText>
				</InfoRow>
			</Container>
		);
	}

	render() {
		const { dispatch, driveItem, galleryInfos, isPersistentlyDownloaded, isRequested, isRequired, version } = this.props;
		const { template } = galleryInfos;
		const title = DriveItemUtils.getDisplayName(driveItem);
		const itemTypeKey = DriveItemUtils.getAssetTypeTranslationKey(driveItem.type, driveItem.format);
		const subtitle = I18n.t(itemTypeKey);
		const fileSize = driveItem.originalFileSize ? Utils.formatFileSize(driveItem.originalFileSize) : null;
		const description = DriveItemUtils.getDescription(driveItem, template);
		const extension = DriveItemUtils.getExtension(driveItem);
		const locale = DriveItemUtils.getLocale(driveItem);
		const { sharedMethods } = template;

		const hasDownloadRights = sharedMethods && sharedMethods.includes(Api.ShareMethod.Download);

		const isDesktop = UserAgentUtils.isDesktop();
		const isMobileApp = UserAgentUtils.isMobileApp;
		const isOnline = UserAgentUtils.isOnline();
		const isSafariMobile = UserAgentUtils.isSafari() && (!isDesktop && !isMobileApp);

		let isReadable = false;
		let isDownloadable = false;

		switch (driveItem.type) {
			case Api.DriveItemType.Directory:
				isReadable = true;
				isDownloadable = false;
				break;
			case Api.DriveItemType.File:
				isReadable = isOnline || isPersistentlyDownloaded ? true : false;
				isDownloadable = isOnline && hasDownloadRights && (!isMobileApp || !isPersistentlyDownloaded) ? true : false;
				break;
			case Api.DriveItemType.Image:
			case Api.DriveItemType.Movie:
			case Api.DriveItemType.Sound:
			case Api.DriveItemType.Publication:
				isReadable = isOnline || isPersistentlyDownloaded || driveItem.type === Api.DriveItemType.Image ? true : false;
				isDownloadable = isOnline && hasDownloadRights && (!isMobileApp || !isPersistentlyDownloaded) && !isSafariMobile
					? true
					: false; // Safari Mobile opens assets instead of downloading them
				break;
			case Api.DriveItemType.ExternalLink:
				isReadable = isOnline;
				isDownloadable = false;
				break;
			case Api.DriveItemType.Component:
				isReadable = isOnline || isPersistentlyDownloaded ? true : false;
				isDownloadable = isOnline && isMobileApp && !isPersistentlyDownloaded;
				break;
			default:
				break;
		}

		const isUpdatable = isOnline && hasDownloadRights && isPersistentlyDownloaded && version < driveItem.version;
		const hasActionButtons = isReadable || isDownloadable || isUpdatable || isPersistentlyDownloaded;

		const publicationCovers = this.getPublicationCovers();

		return (
			<StyledDriveItemDetail>
				<Header>
					<Container>
						<HeaderInner>
							{this.getImgElement()}
							<TitleContainer>
								<Title>{title}</Title>
								<SubTitle>{subtitle}</SubTitle>
							</TitleContainer>
						</HeaderInner>
					</Container>
				</Header>
				{hasActionButtons &&
					<ActionButtons>
						<Container>
							<ActionButtonsInner>
								{isReadable &&
									<StandardButton onClick={() => dispatch(viewDriveItem(driveItem, DriveItemOrigin.Details))}><Translate value={`messages.${ActionButton.Read}`} /></StandardButton>
								}
								{(isDownloadable || isUpdatable) &&
									<DriveItemDownloadButton driveItems={[driveItem]} style={ActionButtonStyle.Bar} action={isUpdatable ? ActionButton.Update : ActionButton.Download} />
								}
								{isRequired &&
									<StandardButton disabled={true}><Translate value={`messages.downloaded`} /></StandardButton>
								}
								{isRequested &&
									<DriveItemDeleteButton driveItem={driveItem} style={ActionButtonStyle.Bar} />
								}
							</ActionButtonsInner>
						</Container>
					</ActionButtons>
				}
				<div>
					{description &&
						<Container>
							<DescriptionRow>
								<DescriptionLabel><Translate value="messages.description" /></DescriptionLabel>
								<DescriptionText dangerouslySetInnerHTML={{ __html: Utils.nl2br(description) }} />
							</DescriptionRow>
						</Container>
					}
					{extension &&
						this.renderInfoRow('messages.format', extension, Format.Extension)
					}
					{driveItem.resolution &&
						this.renderInfoRow('messages.resolution', `${driveItem.resolution} px`)
					}
					{fileSize && (driveItem.type !== Api.DriveItemType.Publication && driveItem.type !== Api.DriveItemType.Component) &&
						this.renderInfoRow('messages.file_size', fileSize)
					}
					{driveItem.creationDate &&
						this.renderInfoRow('messages.creation_date', driveItem.creationDate, Format.Date)
					}
					{driveItem.lastUpdateDate &&
						this.renderInfoRow('messages.last_update_date', driveItem.lastUpdateDate, Format.Date)
					}
					{driveItem.endDate &&
						this.renderInfoRow('messages.end_date', driveItem.endDate, Format.Date)
					}
					{locale &&
						this.renderInfoRow('messages.language', `languages.${locale}`, Format.Locale)
					}
					{!!publicationCovers.length && (isOnline || isPersistentlyDownloaded) &&
						<Container>
							<PublicationCoverContainer>{publicationCovers}</PublicationCoverContainer>
						</Container>
					}
				</div>
			</StyledDriveItemDetail>
		);
	}
}

const mapStateToProps = (state: StoreState, props: DriveItemDetailContainerProps) => {
	const { galleryInfos, downloadManager, offlineManager } = state;
	const { driveItem } = props;

	const isPersistentlyDownloaded = DownloadManagerUtils.isItemPersistentlyDownloaded(downloadManager, driveItem.id);
	const isRequired = DownloadManagerUtils.isRequiredItem(downloadManager, driveItem.id);
	const isRequested = isPersistentlyDownloaded && DownloadManagerUtils.isRequestedItem(downloadManager, driveItem.id);
	const version = downloadManager.items[driveItem.id] && downloadManager.items[driveItem.id].version;

	return { galleryInfos, offlineManager, isPersistentlyDownloaded, isRequired, isRequested, version };
};

export default connect(mapStateToProps)(DriveItemDetail);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/DriveItem/Detail/index.tsx